import { deferred } from 'fast-defer';
import { createValidateStatus, isMethodIn, updateStaleRequest } from './util';
export function defaultRequestInterceptor(axios) {
    const onFulfilled = async (config) => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        if (config.cache === false) {
            if (__ACI_DEV__) {
                (_a = axios.debug) === null || _a === void 0 ? void 0 : _a.call(axios, {
                    msg: 'Ignoring cache because config.cache is false',
                    data: config
                });
            }
            return config;
        }
        // merge defaults with per request configuration
        config.cache = Object.assign(Object.assign({}, axios.defaults.cache), config.cache);
        if (!isMethodIn(config.method, config.cache.methods)) {
            if (__ACI_DEV__) {
                (_b = axios.debug) === null || _b === void 0 ? void 0 : _b.call(axios, {
                    msg: `Ignored because method (${config.method}) is not in cache.methods (${config.cache.methods})`
                });
            }
            return config;
        }
        const key = (config.id = axios.generateKey(config));
        // Assumes that the storage handled staled responses
        let cache = await axios.storage.get(key);
        // Not cached, continue the request, and mark it as fetching
        emptyOrStale: if (cache.state === 'empty' || cache.state === 'stale') {
            /**
             * This checks for simultaneous access to a new key. The js event loop jumps on the
             * first await statement, so the second (asynchronous call) request may have already
             * started executing.
             */
            if (axios.waiting[key]) {
                cache = (await axios.storage.get(key));
                /**
                 * This check is required when a request has it own cache deleted manually, lets
                 * say by a `axios.storage.delete(key)` and has a concurrent loading request.
                 * Because in this case, the cache will be empty and may still has a pending key
                 * on waiting map.
                 */
                //@ts-expect-error read above
                if (cache.state !== 'empty') {
                    if (__ACI_DEV__) {
                        (_c = axios.debug) === null || _c === void 0 ? void 0 : _c.call(axios, {
                            id: key,
                            msg: 'Waiting list had an deferred for this key, waiting for it to finish'
                        });
                    }
                    break emptyOrStale;
                }
            }
            // Create a deferred to resolve other requests for the same key when it's completed
            axios.waiting[key] = deferred();
            /**
             * Add a default reject handler to catch when the request is aborted without others
             * waiting for it.
             */
            (_d = axios.waiting[key]) === null || _d === void 0 ? void 0 : _d.catch(() => undefined);
            await axios.storage.set(key, {
                state: 'loading',
                previous: cache.state,
                // Eslint complains a lot :)
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                data: cache.data,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
                createdAt: cache.createdAt
            });
            if (cache.state === 'stale') {
                updateStaleRequest(cache, config);
                if (__ACI_DEV__) {
                    (_e = axios.debug) === null || _e === void 0 ? void 0 : _e.call(axios, {
                        id: key,
                        msg: 'Updated stale request'
                    });
                }
            }
            config.validateStatus = createValidateStatus(config.validateStatus);
            if (__ACI_DEV__) {
                (_f = axios.debug) === null || _f === void 0 ? void 0 : _f.call(axios, {
                    id: key,
                    msg: 'Sending request, waiting for response'
                });
            }
            return config;
        }
        let cachedResponse;
        if (cache.state === 'loading') {
            const deferred = axios.waiting[key];
            // Just in case, the deferred doesn't exists.
            /* istanbul ignore if 'really hard to test' */
            if (!deferred) {
                await axios.storage.remove(key);
                return config;
            }
            if (__ACI_DEV__) {
                (_g = axios.debug) === null || _g === void 0 ? void 0 : _g.call(axios, {
                    id: key,
                    msg: 'Detected concurrent request, waiting for it to finish'
                });
            }
            try {
                cachedResponse = await deferred;
            }
            catch (err) {
                if (__ACI_DEV__) {
                    (_h = axios.debug) === null || _h === void 0 ? void 0 : _h.call(axios, {
                        id: key,
                        msg: 'Deferred rejected, requesting again',
                        data: err
                    });
                }
                // The deferred is rejected when the request that we are waiting rejected cache.
                return config;
            }
        }
        else {
            cachedResponse = cache.data;
        }
        // Even though the response interceptor receives this one from here,
        // it has been configured to ignore cached responses = true
        config.adapter = () => Promise.resolve({
            config,
            data: cachedResponse.data,
            headers: cachedResponse.headers,
            status: cachedResponse.status,
            statusText: cachedResponse.statusText,
            cached: true,
            id: key
        });
        if (__ACI_DEV__) {
            (_j = axios.debug) === null || _j === void 0 ? void 0 : _j.call(axios, {
                id: key,
                msg: 'Returning cached response'
            });
        }
        return config;
    };
    return {
        onFulfilled,
        apply: () => axios.interceptors.request.use(onFulfilled)
    };
}
